import React, { useState } from "react";
import { ResetPasswordSchema } from "../../../components/Helpers";
import { Formik, Form } from "formik";
import { FaRegEyeSlash, FaEye, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import TextInput from "../../../components/TextInput";
import datacloud from "../../../Assets/unisoft.jpg";
import { resetPassword as resetPasswordApi } from "../../../Services/authapis";

function ResetForm() {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [strengthText, setStrengthText] = useState('');
  const [strengthColor, setStrengthColor] = useState('');
  const [strengthCriteria, setStrengthCriteria] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  });
  const [loading, setLoading] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();

  const checkPasswordStrength = (password) => {
    let strength = 0;
    const criteria = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
      hasMinLength: password.length >= 8,
    };

    if (password.length > 0) {
      if (criteria.hasMinLength) strength += 20;
      if (criteria.hasLowercase) strength += 20;
      if (criteria.hasUppercase) strength += 20;
      if (criteria.hasNumber) strength += 20;
      if (criteria.hasSpecialChar) strength += 20;
    }

    let text = '';
    let color = '';

    if (password.length === 0) {
      text = '';
      color = '';
    } else if (strength <= 40) {
      text = 'Weak';
      color = 'bg-red-500';
    } else if (strength <= 80) {
      text = 'Medium';
      color = 'bg-yellow-500';
    } else {
      text = 'Strong';
      color = 'bg-green-500';
    }

    setPasswordStrength(strength);
    setStrengthText(text);
    setStrengthColor(color);
    setStrengthCriteria(criteria);
  };

  const renderStrengthIndicator = (newPassword) => {
    if (newPassword.length === 0) return null;

    return (
      <div className="mt-2 p-2 border border-gray-200 rounded-md">
        <div className="flex justify-between items-center mb-2">
          <p className="text-sm font-medium text-gray-700">Password strength:</p>
          <p className={`text-sm font-semibold ${strengthColor.replace('bg-', 'text-')}`}>
            {strengthText}
          </p>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
          <div
            className={`h-2.5 rounded-full ${strengthColor}`}
            style={{ width: `${passwordStrength}%` }}
          ></div>
        </div>
        <div className="grid grid-cols-2 gap-y-1 text-sm">
          <div className={`flex items-center ${strengthCriteria.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
            {strengthCriteria.hasLowercase ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
            Lowercase letter
          </div>
          <div className={`flex items-center ${strengthCriteria.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
            {strengthCriteria.hasUppercase ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
            Uppercase letter
          </div>
          <div className={`flex items-center ${strengthCriteria.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
            {strengthCriteria.hasNumber ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
            Number
          </div>
          <div className={`flex items-center ${strengthCriteria.hasSpecialChar ? 'text-green-600' : 'text-red-500'}`}>
            {strengthCriteria.hasSpecialChar ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
            Special character
          </div>
          <div className={`flex items-center ${strengthCriteria.hasMinLength ? 'text-green-600' : 'text-red-500'}`}>
            {strengthCriteria.hasMinLength ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
            8+ characters
          </div>
        </div>
      </div>
    );
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      if (values.newPassword !== values.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      await resetPasswordApi(token, {
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      setIsSubmitted(true);
      toast.success("Password reset has been done successfully");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to reset password";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-[80vw] sm:w-[60vw] md:w-[24vw] p-3 md:px-5 bg-white rounded-2xl h-[50vh] sm:h-[55vh]">
      {isSubmitted ? (
        <div className="text-center w-full">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
            Password Reset Successful
          </h1>
          <p className="text-md sm:text-lg mb-6">
            Please{" "}
            <Link className="text-primary hover:underline" to={"/login"}>
              Click Here
            </Link>{" "}
            to login.
          </p>
        </div>
      ) : (
        <Formik
          initialValues={{
            newPassword: "",
            confirmPassword: "",
            token: token,
          }}
          validationSchema={ResetPasswordSchema}
          onSubmit={handleResetPassword}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form className="shadow-none w-full h-full mt-5 sm:mt-10">
              <div className="text-center mb-6 sm:mb-8">
                <span className="flex items-center justify-center p-4">
                  <img src={datacloud} alt="Logo" className="h-16 sm:h-20 w-auto object-contain" />
                </span>
                <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
                  Reset Your Password
                </h1>
                <p className="text-md sm:text-lg flex items-center justify-center mb-6 sm:mb-10">
                  Please enter your new password
                </p>
              </div>

              {/* New Password Field */}
              <div className="relative my-6 sm:my-8">
                <TextInput
                  name="newPassword"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="New Password"
                  handleChange={(e) => {
                    handleChange(e);
                    checkPasswordStrength(e.target.value);
                  }}
                  value={values.newPassword}
                  className="w-full border border-gray-300 rounded-[0.3rem] p-2 h-[2.6rem] bg-white"
                  legend
                />
                <div
                  className="absolute right-2 top-3 flex items-center px-2 cursor-pointer"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {!isPasswordVisible ? <FaRegEyeSlash /> : <FaEye />}
                </div>
                {errors.newPassword && touched.newPassword && (
                  <p className="text-red-400">{errors.newPassword}</p>
                )}
                {/* Password Strength Indicator */}
                {renderStrengthIndicator(values.newPassword)}
              </div>

              {/* Confirm Password Field */}
              <div className="relative my-6 sm:my-8">
                <TextInput
                  name="confirmPassword"
                  value={values.confirmPassword}
                  placeholder="Confirm Password"
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  handleChange={handleChange}
                  className="w-full border border-gray-300 rounded-[0.3rem] p-2 h-[2.6rem] bg-white"
                  legend
                />
                <div
                  className="absolute right-2 top-3 flex items-center px-2 cursor-pointer"
                  onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                >
                  {!isConfirmPasswordVisible ? <FaRegEyeSlash /> : <FaEye />}
                </div>
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="text-red-400">{errors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full text-white font-medium text-lg bg-primary hover:bg-primaryDark p-2 rounded"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default ResetForm;
